import { Injectable } from '@angular/core'
import { debounceTime, firstValueFrom, fromEvent } from 'rxjs'
import { SizesService, VERTICAL_SPACER_PX } from '../sizes/sizes.service'
import { SimpleWindowRefService } from '../window/simple-window-ref.service'

/**
 * Note. If we attempt to scroll from y = 1.35 to 1.36, some browsers avoid scrolling,
 * thus never firing a scroll event
 */
const SCROLL_TOLERANCE = 1

/**
 * A scroll anchor, is an element, and an offset from the top of the page.
 *
 * This any consumers of this service, can request to scroll to the anchor after, for example, their content loads.
 *
 * Note: Chrome/Edgium has this support natively, other browsers do not.
 */
export type ScrollAnchor = [HTMLElement, number]

/**
 * Can be used on any page to handle scrolling to the correct location
 */
@Injectable({
  providedIn: 'root',
})
export class ScrollService {
  // Tracks which element we are scrolling to.
  // Used to avoid scrolling after async calls when the scrollto has changed
  // Note: This could possibly be changed to a selector if it becomes useful in the future
  private scrollingTo = ''

  scrollAnchor?: ScrollAnchor

  constructor(
    private readonly sizesService: SizesService,
    private readonly windowRef: SimpleWindowRefService,
  ) {}

  scrollToUnderHeaderById(id: string, smooth: boolean): Promise<Event | undefined> {
    const el: Element | null = this.windowRef.document().getElementById(id)
    if (el === null) {
      return Promise.reject('Element was not rendered: ' + id)
    }
    return this.scrollToUnderHeader(el, smooth)
  }

  scrollToUnderHeader(el: Element, smooth: boolean): Promise<Event | undefined> {
    const offset = this.sizesService.getCurrentHeaderSizeWithOffsetPx()
    return this.scrollToWithOffsetAsync(el, offset ? offset : VERTICAL_SPACER_PX, smooth)
  }

  async scrollToWithOffsetAsync(el: Element, offset: number, smooth: boolean): Promise<Event | undefined> {
    if (this.isEqualWithinTolerance(el.getBoundingClientRect().top, offset, SCROLL_TOLERANCE)) {
      return
    }

    const scrollAwait = this.awaitNextScrollEnd()
    this.scrollToWithOffset(el, offset, smooth)
    return scrollAwait
  }

  scrollToWithOffset(el: Element, offset: number, smooth: boolean): void {
    if (this.windowRef.pageIsVisible()) {
      const dims = el.getBoundingClientRect()
      const top = dims.top + this.windowRef.scrollY() - offset
      this.windowRef.scrollTo(top, undefined, smooth)
    }
  }

  /**
   *   Waits for next scroll to end, since scroll events arent singular. we wait until theres a reasonable time
   *   between scroll events
   *
   *   Must be called *BEFORE* triggering a scroll event...
   *
   *   Also, Make sure the browsers actually going to scroll, some browsers will not trigger a scroll event if they

   *   are in the right place already, essentially causing this to never return
   *
   *   Also: do not do `await awaitNextScrollEnd()` or bad things will happen
   */
  awaitNextScrollEnd(idleTime: number = 50): Promise<Event> {
    return firstValueFrom(fromEvent(this.windowRef.document(), 'scroll').pipe(debounceTime(idleTime)))
  }

  isScrollingTo(id: string): boolean {
    return this.scrollingTo === id
  }

  setScrollingTo(id: string): void {
    this.scrollingTo = id
  }

  getScrollingTo(): string {
    return this.scrollingTo
  }

  clearScrollingTo(): void {
    this.scrollingTo = ''
  }

  async scrollToAnchorPoint(): Promise<Event | undefined> {
    if (!this.scrollAnchor || this.scrollingTo !== '') {
      return
    }

    const [el, offset] = this.scrollAnchor
    return this.scrollToWithOffsetAsync(el, offset, false)
  }

  clearScrollingAnchor(): void {
    this.scrollAnchor = undefined
  }

  // We only set scroll anchors when the top of the div is actually visible (eg, under the header)
  setScrollAnchorToId(id: string): void {
    const nativeElement = this.windowRef.document().getElementById(id)
    if (nativeElement) {
      const top = nativeElement.getBoundingClientRect().top
      const currentPlaybackHeaderSizePx = this.sizesService.getCurrentPlaybackHeaderSizePx()
      const offset = currentPlaybackHeaderSizePx ? currentPlaybackHeaderSizePx : 0
      if (top > offset && top < this.windowRef.getViewportHeight()) {
        this.scrollAnchor = [nativeElement, top]
      }
    }
  }

  getAnchorId(): string | undefined {
    return this.scrollAnchor ? this.scrollAnchor[0].id : undefined
  }

  private isEqualWithinTolerance(valueA: number, valueB: number, tolerance: number): boolean {
    return Math.abs(valueA - valueB) < tolerance
  }
}
